import * as Tone from "tone";
import type { AudioData } from "../types/model.types";
import { StreamFormat } from "../types/model.types";
import {
  AudioInitializationError,
  PlayerError,
  SDKError,
  withErrorData,
} from "../types/error.types";
import { FrequencySampler } from "./frequency-sampler";
import { isLiveFormat } from "../utils/type-helpers";
import { debugConfig } from "../utils/debug-config";
import { isIOS, isMobile, isAndroid } from "../utils/device-detection";

export type AudioGraphOptions = {
  onPlaybackFinished?: () => void;
  onFrequencyData?: (frequencyData: number[]) => void;
  onError?: (error: SDKError) => void;
};

/**
 * AudioGraph class that handles all audio-related functionality including:
 * - Managing audio context
 * - Setting up background audio
 * - Processing audio data
 */
export class AudioGraph {
  private options: AudioGraphOptions;
  private audioContext: Tone.Context | null = null;
  private mixerNode: GainNode | null = null;
  private audioElement: HTMLAudioElement | null = null;
  private player: Tone.GrainPlayer | null = null;
  private bufferProcessorNode: AudioWorkletNode | null = null;
  private frequencySampler: FrequencySampler | null = null;
  private analyserNode: AnalyserNode | null = null;
  private setupInProgress: boolean = false;
  private bufferProcessorConfiguredForNonLive: boolean = false;
  private targetLatencyMs: number = 160; // Default latency in milliseconds
  private isMobileDevice: boolean = false;
  private intentionalPause: boolean = false; // Track intentional pauses to prevent iOS reload

  // Performance optimization: Buffer pooling
  private bufferPool: Map<string, AudioBuffer> = new Map();
  private toneBufferPool: Map<string, Tone.ToneAudioBuffer> = new Map();
  private maxPoolSize = 5;

  // Volume state - tracks desired volume level (0 = muted, 1 = full)
  private _volume: number = 1;

  constructor(options: AudioGraphOptions) {
    this.options = options;
    this.isMobileDevice = isMobile();
    this.initAudio();
    this.setupBackgroundAudio();
    this.setupFrequencySampler();
    this.setupVisibilityChangeHandler();
  }

  /**
   * Initializes the audio context and sets up the audio processing pipeline
   */
  private initAudio(): void {
    if (!this.audioContext) {
      try {
        Tone.start();
        const rawContext = new AudioContext({ sampleRate: 48000 });
        const audioContext = new Tone.Context({
          context: rawContext,
          latencyHint: "interactive",
          lookAhead: 0,
        });
        Tone.setContext(audioContext);
        const mixerNode = audioContext.createGain();
        const analyserNode = audioContext.rawContext.createAnalyser();
        mixerNode.connect(analyserNode);
        analyserNode.connect(audioContext.rawContext.destination);

        audioContext.rawContext.onstatechange = () => {
          // Check Tone.Context state (not rawContext.state) as per reference implementation
          if (
            String(audioContext.state) === "interrupted" ||
            String(audioContext.state) === "suspended"
          ) {
            this.resumePlayback();
          }
        };

        this.audioContext = audioContext;
        this.mixerNode = mixerNode;
        this.analyserNode = analyserNode;

        console.log("Audio context initialized successfully");
      } catch (error) {
        console.error("Failed to initialize audio context:", error);
        const audioContextError = withErrorData(
          AudioInitializationError.AudioContextCreationFailed,
          {
            originalError:
              error instanceof Error
                ? error.message
                : "Unknown audio context error",
          },
        );
        throw audioContextError;
      }
    }
  }

  private async setupLiveAudio(audioContext: Tone.Context): Promise<void> {
    // Prevent multiple setup attempts
    if (this.bufferProcessorNode) {
      console.log("AudioWorklet already loaded, skipping setup");
      return;
    }

    try {
      // buffer-processor.js is copied to dist/ by vite and served from /dist/ by nginx
      // Path: public/buffer-processor.js -> dist/buffer-processor.js -> /dist/buffer-processor.js
      const bufferProcessorPath = "/buffer-processor.js";

      console.log(
        "Attempting to load buffer-processor AudioWorklet from:",
        bufferProcessorPath,
      );
      await audioContext.rawContext.audioWorklet.addModule(bufferProcessorPath);
      console.log(
        "✅ Successfully loaded buffer-processor from:",
        bufferProcessorPath,
      );

      // Convert latency from ms to frames (at 48kHz sample rate)
      const latencyMinFrames = Math.round(this.targetLatencyMs * 48);

      this.bufferProcessorNode = audioContext.createAudioWorkletNode(
        "buffer-processor",
        {
          processorOptions: {
            latencyMinFrames: latencyMinFrames,
          },
        },
      );

      console.log(
        "BufferProcessorNode created with latency:",
        this.targetLatencyMs,
        "ms (",
        latencyMinFrames,
        "frames)",
      );
      // Don't connect directly to analyserNode or destination - let mixer node handle routing
      // Connection to mixerNode happens below after the try/catch block

      // Send config update message to ensure correct latency and monitoring config are set
      if (this.bufferProcessorNode.port) {
        this.bufferProcessorNode.port.postMessage({
          action: "updateConfig",
          config: {
            latencyMinFrames: latencyMinFrames,
            enableMonitoring: debugConfig.isStreamMonitoringEnabled(),
          },
        });
        console.log(
          "Sent config update to buffer processor:",
          this.targetLatencyMs,
          "ms (",
          latencyMinFrames,
          "frames), monitoring:",
          debugConfig.isStreamMonitoringEnabled(),
        );
      }

      console.log("Buffer-processor AudioWorklet loaded successfully");
    } catch (error) {
      console.error("Failed to load buffer-processor AudioWorklet:", error);

      this.handleAudioError(AudioInitializationError.WorkletModuleLoadFailed, {
        originalError: error instanceof Error ? error.message : String(error),
        context: "setupLiveAudio",
        secureContextRequired: String(error).includes("secure context"),
        timestamp: new Date().toISOString(),
      });

      // Set buffer processor to null to indicate failure
      this.bufferProcessorNode = null;
    }

    // Ensure mixer node is available before connecting
    if (this.bufferProcessorNode && this.mixerNode) {
      this.bufferProcessorNode.connect(this.mixerNode);
    } else if (this.bufferProcessorNode && !this.mixerNode) {
      // Buffer processor was created successfully but mixer node is missing - this is an error
      this.handleAudioError(
        AudioInitializationError.AudioContextCreationFailed,
        {
          context: "setupLiveAudio",
          reason: "Mixer node is null but buffer processor was created",
          bufferProcessorCreated: true,
          mixerNodeAvailable: false,
          timestamp: new Date().toISOString(),
        },
      );
    }
    // If bufferProcessorNode is null, error was already handled in catch block above
  }
  /**
   * Configures audio element for mobile devices with silence.mp3
   */
  private configureMobileAudioElement(audioElement: HTMLAudioElement): void {
    audioElement.src = "/silence.mp3";
    audioElement.loop = false;

    // Set up event handlers for mobile
    audioElement.onloadeddata = () => {
      console.log("Silence audio loaded for mobile playback");
    };

    audioElement.onerror = (e) => {
      console.error("Silence audio failed to load:", audioElement.error?.message || e);
    };

    // On iOS, audio will tear down if returning from background after playing audio from a different application
    // To fix this we must reload the page which will enable audio to be played again
    // However, if the pause was intentional (user paused from app), don't reload - just clear lock screen
    if (isIOS()) {
      audioElement.onpause = () => {
        if (document.hidden && !this.intentionalPause) {
          location.reload();
        }
      };
    }

    // Connect audio element to mixer node for mobile lock screen playback
    if (this.audioContext && this.mixerNode) {
      const source = this.audioContext.createMediaElementSource(audioElement);
      source.connect(this.mixerNode);
    }
  }

  /**
   * Configures audio element for non-mobile devices
   */
  private configureDesktopAudioElement(audioElement: HTMLAudioElement): void {
    if (this.audioContext?.rawContext instanceof AudioContext) {
      const destinationNode =
        this.audioContext.rawContext.createMediaStreamDestination();
      audioElement.srcObject = destinationNode.stream;
    }
  }

  /**
   * Ensures the silence audio element is playing (for mobile lock screen controls)
   */
  private ensureSilenceAudioPlaying(): void {
    // On iOS, don't restart silence audio if user intentionally paused
    // This keeps the lock screen controls cleared as intended
    if (isIOS() && this.intentionalPause) {
      return;
    }

    if (this.isMobileDevice && this.audioElement) {
      // Reset intentional pause flag since we're resuming playback
      this.intentionalPause = false;
      this.audioElement.play().catch((error) => {
        // Log but don't propagate - silence.mp3 autoplay failure is expected on mobile
        // due to autoplay policies and is not critical for main audio playback.
        // The silence audio is only used for maintaining lock screen controls.
        console.warn(
          "Background silence audio autoplay blocked (expected on mobile):",
          error instanceof Error ? error.message : String(error),
        );
      });
    }
  }

  /**
   * Sets up background audio element for maintaining audio context
   * For mobile devices, uses silence.mp3 to enable lock screen playback
   */
  private setupBackgroundAudio(): void {
    if (document.querySelector("audio") === null && this.audioContext) {
      try {
        this.audioElement = document.createElement("audio");
        this.audioElement.volume = 1;
        this.audioElement.crossOrigin = "anonymous";

        if (this.isMobileDevice) {
          this.configureMobileAudioElement(this.audioElement);
        } else {
          this.configureDesktopAudioElement(this.audioElement);
        }

        document.body.appendChild(this.audioElement);
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : String(error);
        this.handleAudioError(
          AudioInitializationError.AudioContextCreationFailed,
          {
            originalError: errorMessage,
            context: "setupBackgroundAudio",
            timestamp: new Date().toISOString(),
          },
        );
      }
    } else {
      // Audio element already exists, reuse it
      this.audioElement = document.querySelector("audio");

      // For mobile, ensure the audio element is configured correctly
      if (this.isMobileDevice && this.audioElement) {
        if (
          !this.audioElement.src ||
          !this.audioElement.src.includes("silence.mp3")
        ) {
          this.configureMobileAudioElement(this.audioElement);
        }
      }
    }
  }

  /**
   * Handles audio errors by either calling the error callback or logging
   */
  private handleAudioError(
    errorType: PlayerError,
    errorData: Record<string, unknown>,
  ): void {
    const error = withErrorData(errorType, errorData);

    if (this.options.onError) {
      this.options.onError(error);
    } else {
      console.error("Audio error (no handler provided):", error);
    }
  }

  /**
   * Sets up the frequency sampler for audio analysis
   */
  private setupFrequencySampler(): void {
    if (this.analyserNode && this.options.onFrequencyData) {
      this.frequencySampler = new FrequencySampler({
        onFrequencyData: this.options.onFrequencyData,
        sampleNumber: 4,
      });
      this.frequencySampler.initialize(this.analyserNode);
    }
  }

  /**
   * Sets up visibility change handler for mobile devices
   * When user returns from lock screen, ensures mixer gain is set to 1 and resumes playback
   */
  private setupVisibilityChangeHandler(): void {
    if (this.isMobileDevice && typeof document !== "undefined") {
      document.addEventListener("visibilitychange", () => {
        // If user returns from the lock screen on mobile, resume playback and set the gain to 1
        if (!document.hidden) {
          this.resumePlayback();
        }
      });
    }
  }

  /**
   * Checks if the audio context is in a state that requires resuming
   */
  private isContextSuspended(): boolean {
    if (!this.audioContext) return false;
    const state = String(this.audioContext.state);
    return state === "suspended" || state === "interrupted";
  }

  /**
   * Resumes the audio context (both Tone and raw contexts)
   */
  private resumeAudioContext(): void {
    if (!this.audioContext) return;

    this.audioContext.resume().catch((error) => {
      console.error("Failed to resume Tone audio context:", error);
    });

    // Also resume raw context if it's suspended (raw AudioContext doesn't have "interrupted" state)
    if (this.audioContext.rawContext.state === "suspended") {
      this.audioContext.rawContext.resume().catch((error) => {
        console.error("Failed to resume raw audio context:", error);
      });
    }
  }

  /**
   * Ensures all audio components are ready for playback
   */
  private ensurePlaybackReady(): void {
    // Resume player if it exists and isn't started
    if (this.player && this.player.state !== "started") {
      this.player.start();
    }

    // Ensure audio element volume is set
    if (this.audioElement) {
      this.audioElement.volume = 1;
    }

    // Ensure mixer gain respects the current volume setting
    if (this.mixerNode) {
      this.mixerNode.gain.value = this._volume;
    }

    // For mobile, ensure silence audio element is playing
    this.ensureSilenceAudioPlaying();
  }

  /**
   * Resumes audio playback when context is suspended
   */
  private resumePlayback(): void {
    // Mobile requires manually resuming context on lock, interrupt, etc to continue receiving Tone player input
    if (this.isMobileDevice) {
      Tone.start();
      this.resumeAudioContext();

      if (!this.audioElement) {
        this.setupBackgroundAudio();
      }
    } else {
      // For non-mobile, check and resume if suspended
      if (this.isContextSuspended()) {
        this.audioContext!.resume().catch((error) => {
          console.error("Failed to resume audio context:", error);
          const resumeError = withErrorData(
            AudioInitializationError.AudioContextResumeFailed,
            {
              originalError: error?.message || "Unknown resume error",
            },
          );
          throw resumeError;
        });
      }
    }

    this.ensurePlaybackReady();
  }

  /**
   * Processes audio data - uses buffer-processor for live audio, Tone.GrainPlayer for others
   * @param audioData The audio stream data to process
   * @param streamFormat The format of the stream to determine processing method
   */
  public processAudioData(
    audioData: AudioData,
    streamFormat?: StreamFormat,
  ): void {
    if (!this.audioContext || !this.mixerNode) {
      console.warn("Audio context or mixer node not available");
      return;
    }

    // On Android devices, receiving any audio will auto force it to start playing again.
    // If playbackState is paused, do not add audio data.
    if (
      isAndroid() &&
      typeof navigator !== "undefined" &&
      navigator.mediaSession !== undefined &&
      navigator.mediaSession.playbackState === "paused"
    ) {
      return;
    }

    // Reset intentional pause flag - receiving audio data means we're actively playing
    this.intentionalPause = false;

    // Check if audio context is suspended or interrupted and try to resume
    if (this.isContextSuspended()) {
      this.resumePlayback();
    }

    // Determine if this is live audio based on stream format
    const isLiveAudio = streamFormat ? isLiveFormat(streamFormat) : false;

    // Route all audio through the buffer-processor worklet
    this.processLiveAudio(audioData, !isLiveAudio).catch((error) => {
      console.error("Failed to process audio:", error);
    });

    // Start frequency sampling for live audio
    if (this.frequencySampler && !this.frequencySampler.isRunning) {
      this.frequencySampler.start();
    }
  }

  /**
   * Processes audio using buffer-processor worklet
   * @param audioData The audio data to process
   * @param disableRateAdjustment When true, disables rate adjustment and buffer level targeting (for non-live audio)
   */
  private async processLiveAudio(
    audioData: AudioData,
    disableRateAdjustment: boolean = false,
  ): Promise<void> {
    if (!this.bufferProcessorNode) {
      // Ensure audio context and mixer node are available before setting up live audio
      if (!this.audioContext || !this.analyserNode || !this.mixerNode) {
        console.warn(
          "Audio context, analyser node, or mixer node not available for live audio setup",
        );
        return;
      }

      // Only attempt setup once to prevent repeated calls
      if (!this.setupInProgress) {
        this.setupInProgress = true;
        try {
          console.log("Setting up live audio...");
          await this.setupLiveAudio(this.audioContext);
          console.log(
            "Live audio setup completed, bufferProcessorNode:",
            !!this.bufferProcessorNode,
          );
        } finally {
          this.setupInProgress = false;
        }
      }
    }

    if (this.setupInProgress) {
      return;
    }

    // Ensure buffer processor node is available before processing
    if (!this.bufferProcessorNode) {
      return;
    }

    // Configure buffer-processor for non-live audio (no rate adjustment, no buffer target)
    if (disableRateAdjustment && !this.bufferProcessorConfiguredForNonLive) {
      this.bufferProcessorNode.port.postMessage({
        action: "updateConfig",
        config: {
          disableRateAdjustment: true,
          latencyMinFrames: 0,
        },
      });
      this.bufferProcessorConfiguredForNonLive = true;
    }

    // Create transferable versions of the channel data for zero-copy transfer
    const transferableChannelData = audioData.channelData.map(
      (channel) => channel.buffer,
    );

    // Ensure mixer gain respects the current volume setting
    if (this.mixerNode) {
      this.mixerNode.gain.value = this._volume;
    }

    // Send frames directly to buffer processor using transferable objects
    this.bufferProcessorNode.port.postMessage(
      {
        action: "addBuffer",
        buffer: audioData.channelData,
        uuid: audioData.uuid,
      },
      transferableChannelData,
    );

    // For non-live audio, signal playback finished immediately so StreamQueue dequeues next chunk
    if (disableRateAdjustment) {
      this.options.onPlaybackFinished?.();
    }
  }

  /**
   * Generates a cache key for buffer pooling based on audio data characteristics
   */
  private getBufferCacheKey(audioData: AudioData): string {
    return `${audioData.channelData.length}-${audioData.frameLength}-${audioData.sampleRate}`;
  }

  /**
   * Gets a pooled AudioBuffer or creates a new one
   */
  private getOrCreateAudioBuffer(audioData: AudioData): AudioBuffer {
    const cacheKey = this.getBufferCacheKey(audioData);

    // Try to get from pool first
    if (this.bufferPool.has(cacheKey)) {
      return this.bufferPool.get(cacheKey)!;
    }

    // Create new buffer if not in pool
    const buffer = this.audioContext!.createBuffer(
      audioData.channelData.length,
      audioData.frameLength,
      audioData.sampleRate,
    );

    // Add to pool if we haven't reached max size
    if (this.bufferPool.size < this.maxPoolSize) {
      this.bufferPool.set(cacheKey, buffer);
    }

    return buffer;
  }

  /**
   * Gets a pooled ToneAudioBuffer or creates a new one
   */
  private getOrCreateToneBuffer(
    audioBuffer: AudioBuffer,
  ): Tone.ToneAudioBuffer {
    const cacheKey = `${audioBuffer.numberOfChannels}-${audioBuffer.length}-${audioBuffer.sampleRate}`;

    // Try to get from pool first
    if (this.toneBufferPool.has(cacheKey)) {
      return this.toneBufferPool.get(cacheKey)!;
    }

    // Create new tone buffer
    const toneBuffer = new Tone.ToneAudioBuffer(audioBuffer);

    // Add to pool if we haven't reached max size
    if (this.toneBufferPool.size < this.maxPoolSize) {
      this.toneBufferPool.set(cacheKey, toneBuffer);
    }

    return toneBuffer;
  }

  /**
   * Gets or creates a persistent GrainPlayer instance
   */
  private getOrCreatePlayer(
    toneBuffer: Tone.ToneAudioBuffer,
  ): Tone.GrainPlayer {
    // Reuse existing player if available, otherwise create new one
    if (!this.player) {
      this.player = new Tone.GrainPlayer({
        url: toneBuffer,
        grainSize: 0.12,
        overlap: 0.06,
      });
    } else {
      // Update buffer for existing player
      this.player.buffer = toneBuffer;
    }

    return this.player;
  }

  /**
   * Optimized channel data copying using more efficient methods
   */
  private copyChannelDataOptimized(
    buffer: AudioBuffer,
    audioData: AudioData,
  ): void {
    const { channelData } = audioData;

    // Helper function to ensure ArrayBuffer-backed Float32Array
    const ensureArrayBuffer = (
      arr: Float32Array,
    ): Float32Array<ArrayBuffer> => {
      // If already backed by ArrayBuffer, return as-is with type assertion
      if (arr.buffer instanceof ArrayBuffer) {
        return arr as any as Float32Array<ArrayBuffer>;
      }
      // Otherwise, create a new ArrayBuffer-backed copy
      return new Float32Array(arr) as any as Float32Array<ArrayBuffer>;
    };

    // Use Promise-based approach for large buffers to avoid blocking
    if (audioData.frameLength > 48000) {
      // More than 1 second at 48kHz
      // For large buffers, use requestIdleCallback to avoid blocking
      if (typeof requestIdleCallback !== "undefined") {
        requestIdleCallback(() => {
          for (let channel = 0; channel < channelData.length; channel++) {
            buffer.copyToChannel(
              ensureArrayBuffer(channelData[channel]),
              channel,
            );
          }
        });
      } else {
        // Fallback for browsers without requestIdleCallback
        setTimeout(() => {
          for (let channel = 0; channel < channelData.length; channel++) {
            buffer.copyToChannel(
              ensureArrayBuffer(channelData[channel]),
              channel,
            );
          }
        }, 0);
      }
    } else {
      // For small buffers, copy synchronously
      for (let channel = 0; channel < channelData.length; channel++) {
        buffer.copyToChannel(ensureArrayBuffer(channelData[channel]), channel);
      }
    }
  }

  /**
   * Processes non-live audio using Tone.GrainPlayer with performance optimizations
   * @deprecated Currently unused — all audio routes through buffer-processor. Kept for easy rollback.
   */
  // @ts-ignore TS6133 — kept for easy rollback to GrainPlayer path
  private _processNonLiveAudio(audioData: AudioData): void {
    // Get or create optimized buffer
    const buffer = this.getOrCreateAudioBuffer(audioData);

    // Copy channel data with optimization
    this.copyChannelDataOptimized(buffer, audioData);

    // Get or create tone buffer
    const toneBuffer = this.getOrCreateToneBuffer(buffer);

    // Get or create player (reuses existing player if available)
    const player = this.getOrCreatePlayer(toneBuffer);

    player.playbackRate = audioData.playbackRate;
    if (this.mixerNode) {
      player.connect(this.mixerNode);
      // Ensure mixer gain respects the current volume setting
      this.mixerNode.gain.value = this._volume;
    }

    player.onstop = () => {
      this.frequencySampler?.stop();
      this.options.onPlaybackFinished?.();
    };

    try {
      player.start();
    } catch (error) {
      console.error("Failed to start player:", error);
    }
  }

  /**
   * Stops the current audio playback
   * On mobile, keeps the audio element playing (but muted via mixer gain) to maintain MediaSession controls
   * The audio element must remain playing for MediaSession to stay active on mobile devices
   */
  public stop(): void {
    if (this.player) {
      this.player.stop();
      this.player.disconnect();
      this.player = null;
    }

    if (this.bufferProcessorNode) {
      // Stop the buffer processor before disconnecting
      if (this.bufferProcessorNode.port) {
        this.bufferProcessorNode.port.postMessage({
          action: "stop",
        });
      }
      this.bufferProcessorNode.disconnect();
      this.bufferProcessorNode = null;
      this.bufferProcessorConfiguredForNonLive = false;
    }

    if (this.mixerNode) {
      this.mixerNode.gain.value = 0;
    }

    // On iOS, pause the audio element to clear lock screen controls entirely
    // This is intentional - lock screen controls won't work anyway due to iOS limitations
    // On Android, keep the audio element playing to maintain MediaSession controls
    if (isIOS() && this.audioElement) {
      this.intentionalPause = true;
      this.audioElement.pause();
    }

    // Stop frequency sampling
    this.frequencySampler?.stop();
  }

  /**
   * Sets the volume of the audio output
   * @param volume Volume level (0.0 to 1.0)
   */
  public setVolume(volume: number): void {
    this._volume = Math.max(0, Math.min(1, volume));
    if (this.mixerNode) {
      this.mixerNode.gain.value = this._volume;
    }
  }

  /**
   * Sets the target latency for the buffer processor
   * @param latencyMs Latency in milliseconds
   */
  public setTargetLatency(latencyMs: number): void {
    this.targetLatencyMs = latencyMs;
    console.log(`Target latency set to: ${latencyMs}ms`);

    // If buffer processor is already created, update it immediately
    if (this.bufferProcessorNode && this.bufferProcessorNode.port) {
      const latencyMinFrames = Math.round((latencyMs / 1000) * 48000);
      console.log(
        `Updating existing buffer processor latency to: ${latencyMs}ms (${latencyMinFrames} frames)`,
      );
      this.bufferProcessorNode.port.postMessage({
        action: "updateConfig",
        config: {
          latencyMinFrames: latencyMinFrames,
          enableMonitoring: debugConfig.isStreamMonitoringEnabled(),
        },
      });
    }
  }

  /**
   * Gets the current volume level
   * @returns Current volume level (0.0 to 1.0)
   */
  public getVolume(): number {
    return this.mixerNode?.gain.value || 0;
  }

  /**
   * Starts the silence audio element for mobile lock screen controls.
   * Should be called once at playback start when user gesture is valid.
   */
  public startSilenceAudio(): void {
    this.ensureSilenceAudioPlaying();
  }

  /**
   * Checks if the audio context is ready
   * @returns True if audio context is available and running
   */
  public isReady(): boolean {
    return this.audioContext !== null && this.audioContext.state === "running";
  }

  /**
   * Gets the audio context instance
   * @returns The Tone.Context instance or null
   */
  public getAudioContext(): Tone.Context | null {
    return this.audioContext;
  }

  /**
   * Gets the mixer node
   * @returns The GainNode mixer or null
   */
  public getMixerNode(): GainNode | null {
    return this.mixerNode;
  }

  /**
   * Gets the audio element
   * @returns The HTMLAudioElement or null
   */
  public getAudioElement(): HTMLAudioElement | null {
    return this.audioElement;
  }

  /**
   * Destroys the audio graph and releases all resources
   */
  public destroy(): void {
    console.debug("AudioGraph.destroy");
    this.stop();

    // Clean up buffer pools
    this.bufferPool.clear();
    this.toneBufferPool.clear();

    if (this.frequencySampler) {
      this.frequencySampler.destroy();
      this.frequencySampler = null;
    }

    if (this.audioElement) {
      // For mobile, pause the audio element before removing to maintain MediaSession
      if (this.isMobileDevice) {
        this.audioElement.pause();
        document.querySelector("audio")?.remove();
      }
      this.audioElement = null;
    }

    if (this.audioContext) {
      this.audioContext.close();
      this.audioContext = null;
    }

    this.mixerNode = null;
    this.analyserNode = null;
  }
}
